import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { User, Company, LoginCredentials, AuthResponse, CompanyRegistration } from '../types';
import { authAPI } from '../services/api';
import api from '../services/api';

// ── Billing status types ──────────────────────────────────────────────────────
export interface BillingStatus {
  status: 'active' | 'grace_period' | 'locked';
  gracePeriodEndsAt: string | null;
  lockedAt: string | null;
  daysUntilLock: number | null;
}

interface AuthContextType {
  user: User | null;
  company: Company | null;
  token: string | null;
  isImpersonating: boolean;
  billingStatus: BillingStatus | null;
  login: (credentials: LoginCredentials) => Promise<User>;
  logout: () => Promise<void>;
  stopImpersonating: () => void;
  registerCompany: (data: CompanyRegistration) => Promise<void>;
  refreshBillingStatus: () => Promise<void>;
  loading: boolean;
  isAuthenticated: boolean;
  hasPermission: (permission: string) => boolean;
  isRole: (role: string | string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// Roles that need billing status checks
const COMPANY_ROLES = ['company_admin', 'fleet_manager', 'driver', 'supervisor'];

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser]               = useState<User | null>(null);
  const [company, setCompany]         = useState<Company | null>(null);
  const [token, setToken]             = useState<string | null>(null);
  const [loading, setLoading]         = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [billingStatus, setBillingStatus]     = useState<BillingStatus | null>(null);

  // ── Fetch billing status ────────────────────────────────────────────────────
  const refreshBillingStatus = useCallback(async () => {
    try {
      const res = await api.get('/auth/billing-status');
      setBillingStatus(res.data.data);
    } catch {
      // Fail silently — don't block app on billing check failure
    }
  }, []);

  useEffect(() => {
    const initializeAuth = async () => {
      const params = new URLSearchParams(window.location.search);
      const impersonateToken = params.get('impersonate');

      if (impersonateToken) {
        params.delete('impersonate');
        const newUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : '');
        window.history.replaceState({}, '', newUrl);

        try {
          const originalToken   = localStorage.getItem('token');
          const originalUser    = localStorage.getItem('user');
          const originalCompany = localStorage.getItem('company');

          if (originalToken) {
            sessionStorage.setItem('original_token',   originalToken);
            sessionStorage.setItem('original_user',    originalUser   || '');
            sessionStorage.setItem('original_company', originalCompany || '');
          }

          localStorage.setItem('token', impersonateToken);
          const currentUserData = await authAPI.getCurrentUserWithCompany();

          localStorage.setItem('token',   impersonateToken);
          localStorage.setItem('user',    JSON.stringify(currentUserData.user));
          if (currentUserData.company) {
            localStorage.setItem('company', JSON.stringify(currentUserData.company));
          }

          setToken(impersonateToken);
          setUser(currentUserData.user);
          setCompany(currentUserData.company ?? null);
          setIsImpersonating(true);
          setLoading(false);

          // Fetch billing status for impersonated company user
          if (currentUserData.user && COMPANY_ROLES.includes(currentUserData.user.role)) {
            if (currentUserData.company?.billing) {
              setBillingStatus({
                status: currentUserData.company.billing.status || 'active',
                gracePeriodEndsAt: currentUserData.company.billing.gracePeriodEndsAt || null,
                lockedAt: currentUserData.company.billing.lockedAt || null,
                daysUntilLock: null,
              });
            } else {
              await refreshBillingStatus();
            }
          }
          return;
        } catch (err) {
          console.error('Impersonation failed:', err);
          localStorage.removeItem('token');
        }
      }

      // ── Normal auth initialization ──────────────────────────────────────────
      const storedToken   = localStorage.getItem('token');
      const storedUser    = localStorage.getItem('user');
      const storedCompany = localStorage.getItem('company');

      const wasImpersonating = sessionStorage.getItem('original_token');
      if (wasImpersonating) setIsImpersonating(true);

      if (storedToken && storedUser) {
        try {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
          if (storedCompany) {
            const parsedCompany = JSON.parse(storedCompany);
            setCompany(parsedCompany);
            // Set billing immediately from cache — no API call needed
            if (parsedCompany?.billing?.status) {
              setBillingStatus({
                status: parsedCompany.billing.status,
                gracePeriodEndsAt: parsedCompany.billing.gracePeriodEndsAt || null,
                lockedAt: parsedCompany.billing.lockedAt || null,
                daysUntilLock: null,
              });
            }
          } else {
            setCompany(null);
          }

          const currentUserData = await authAPI.getCurrentUserWithCompany();
          setUser(currentUserData.user);

          if (currentUserData.company) {
            setCompany(currentUserData.company);
            localStorage.setItem('company', JSON.stringify(currentUserData.company));
            
            if (currentUserData.user && COMPANY_ROLES.includes(currentUserData.user.role)) {
              if (currentUserData.company.billing?.status) {
                setBillingStatus({
                  status: currentUserData.company.billing.status,
                  gracePeriodEndsAt: currentUserData.company.billing.gracePeriodEndsAt || null,
                  lockedAt: currentUserData.company.billing.lockedAt || null,
                  daysUntilLock: null,
                });
              } else {
                await refreshBillingStatus();
              }
            }
          } else {
            setCompany(null);
            localStorage.removeItem('company');
          }
        } catch (error) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('company');
          setToken(null);
          setUser(null);
          setCompany(null);
        }
      }

      setLoading(false);
    };

    initializeAuth();
  }, [refreshBillingStatus]);

  // ── Poll billing status every 5 minutes for company users ──────────────────
  useEffect(() => {
    if (!user || !COMPANY_ROLES.includes(user.role)) return;

    const interval = setInterval(refreshBillingStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user, refreshBillingStatus]);

  const persistAuth = (response: AuthResponse) => {
    setToken(response.token);
    setUser(response.user);
    setCompany(response.company ?? null);

    localStorage.setItem('token', response.token);
    localStorage.setItem('user', JSON.stringify(response.user));

    if (response.company) {
      localStorage.setItem('company', JSON.stringify(response.company));
    } else {
      localStorage.removeItem('company');
    }
  };

  const login = async (credentials: LoginCredentials): Promise<User> => {
    const response: AuthResponse = await authAPI.login(credentials);
    persistAuth(response);

    // Fetch billing status immediately after login for company users
    if (response.user && COMPANY_ROLES.includes(response.user.role)) {
      await refreshBillingStatus();
    }

    return response.user;
  };

  const registerCompany = async (data: CompanyRegistration) => {
    const response: AuthResponse = await authAPI.registerCompany(data);
    persistAuth(response);
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      sessionStorage.removeItem('original_token');
      sessionStorage.removeItem('original_user');
      sessionStorage.removeItem('original_company');

      setToken(null);
      setUser(null);
      setCompany(null);
      setBillingStatus(null);
      setIsImpersonating(false);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('company');
    }
  };

  const stopImpersonating = () => {
    const originalToken   = sessionStorage.getItem('original_token');
    const originalUser    = sessionStorage.getItem('original_user');
    const originalCompany = sessionStorage.getItem('original_company');

    if (!originalToken) {
      logout();
      return;
    }

    localStorage.setItem('token', originalToken);
    if (originalUser)    localStorage.setItem('user',    originalUser);
    else                 localStorage.removeItem('user');
    if (originalCompany) localStorage.setItem('company', originalCompany);
    else                 localStorage.removeItem('company');

    sessionStorage.removeItem('original_token');
    sessionStorage.removeItem('original_user');
    sessionStorage.removeItem('original_company');

    window.location.href = '/tenant/companies';
  };

  const hasPermission = (permission: string): boolean => {
    return user?.permissions?.includes(permission) || false;
  };

  const isRole = (role: string | string[]): boolean => {
    if (!user) return false;
    if (Array.isArray(role)) return role.includes(user.role);
    return user.role === role;
  };

  const value: AuthContextType = {
    user,
    company,
    token,
    isImpersonating,
    billingStatus,
    login,
    logout,
    stopImpersonating,
    registerCompany,
    refreshBillingStatus,
    loading,
    isAuthenticated: !!token && !!user,
    hasPermission,
    isRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};